import React, { useState, useEffect } from 'react';
import { findBlockedTradeReferenceEmailDomain } from '@dill/dill-shared';
import { DillButton, DillModal } from '../../../general/Dill';
import { AppInputField } from '../../../general/AppInputField/AppInputField';
import StateDropDown from './StateDropDown';
import { UploadFileInputs } from './UploadFileInput';
import AddressAutocompleteSelect from './AddressAutocompleteSelect';

const EMPTY_BANK_DETAILS = {
  bankName: '',
  accountNumber: '',
  contactPerson: '',
  phoneNumber: '',
  address: '',
  city: '',
  state: '',
  zip: '',
  email: '',
  authorizedFile: null as File | null,
};

const BankDetailsModal = ({
  open,
  handleClose,
  onSubmit,
  bankReferenceRequiredFields,
  fieldsOptional = false,
  initialValues,
  title,
  submitButtonLabel,
  blockedEmailDomains = [],
  blockedEmailMessage = '',
}: {
  open: boolean;
  handleClose: () => void;
  onSubmit: (details: {
    bankName: string;
    accountNumber: string;
    contactPerson: string;
    phoneNumber: string;
    address: string;
    city: string;
    state: string;
    zip: string;
    email: string;
    authorizedFile?: File | null;
  }) => void;
  bankReferenceRequiredFields: string[];
  fieldsOptional?: boolean;
  /** Pre-fills the form for editing an existing bank reference. Omit/null to add a new one. */
  initialValues?: {
    bankName: string;
    accountNumber: string;
    contactPerson: string;
    phoneNumber: string;
    address: string;
    city: string;
    state: string;
    zip: string;
    email: string;
  } | null;
  title?: string;
  submitButtonLabel?: string;
  // Domains the contact email may not be at — the supplier's and the applicant's own (same list
  // trade references are checked against, see getBlockedTradeReferenceEmailDomains).
  blockedEmailDomains?: string[];
  blockedEmailMessage?: string;
}) => {
  const [bankDetails, setBankDetails] = useState<{
    bankName: string;
    accountNumber: string;
    phoneNumber: string;
    address: string;
    city: string;
    state: string;
    zip: string;
    contactPerson: string;
    email: string;
    authorizedFile?: File | null;
  }>(EMPTY_BANK_DETAILS);
  const [errorFields, setErrorFields] = useState<string[]>([]);
  const [phoneError, setPhoneError] = useState('');
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const phoneRegex = /^\d{3}-\d{3}-\d{4}$/;

  const formatPhone = (raw: string) => {
    const digits = raw.replace(/\D/g, '').slice(0, 10);
    if (digits.length > 6) return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
    if (digits.length > 3) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    return digits;
  };

  useEffect(() => {
    if (open) {
      setBankDetails(
        initialValues ? { ...initialValues, authorizedFile: null } : EMPTY_BANK_DETAILS
      );
      setErrorFields([]);
      setPhoneError('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleInputChange = (field: keyof typeof bankDetails, value: string | File) => {
    setBankDetails((prev) => ({ ...prev, [field]: value }));
  };

  const validateInputs = () => {
    const errors: string[] = [];
    if (!bankDetails.bankName || bankDetails.bankName.trim() === '') errors.push('bankName');
    if (!bankDetails.accountNumber || bankDetails.accountNumber.trim() === '')
      errors.push('accountNumber');
    if (
      (!bankDetails.contactPerson || bankDetails.contactPerson.trim() === '') &&
      bankReferenceRequiredFields.includes('contactPerson') &&
      !fieldsOptional
    ) {
      errors.push('contactPerson');
    }
    if (fieldsOptional) {
      if (bankDetails.email.trim() && !emailRegex.test(bankDetails.email.trim())) {
        errors.push('email');
      }
    } else if (!bankDetails.email.trim() || !emailRegex.test(bankDetails.email.trim())) {
      errors.push('email');
    }
    if (
      !errors.includes('email') &&
      findBlockedTradeReferenceEmailDomain(bankDetails.email.trim(), blockedEmailDomains)
    ) {
      errors.push('email');
    }
    if (
      (!bankDetails.phoneNumber || bankDetails.phoneNumber.trim() === '') &&
      bankReferenceRequiredFields.includes('phoneNumber') &&
      !fieldsOptional
    ) {
      errors.push('phoneNumber');
    } else if (bankDetails.phoneNumber && !phoneRegex.test(bankDetails.phoneNumber.trim())) {
      errors.push('phoneNumber');
      setPhoneError('Must be in XXX-XXX-XXXX format (e.g. 555-123-4567)');
    }
    if (
      (!bankDetails.address || bankDetails.address.trim() === '') &&
      bankReferenceRequiredFields.includes('address') &&
      !fieldsOptional
    ) {
      errors.push('address');
    }
    if (
      (!bankDetails.city || bankDetails.city.trim() === '') &&
      bankReferenceRequiredFields.includes('address') &&
      !fieldsOptional
    ) {
      errors.push('city');
    }
    if (
      (!bankDetails.state || bankDetails.state.trim() === '') &&
      bankReferenceRequiredFields.includes('address') &&
      !fieldsOptional
    ) {
      errors.push('state');
    }
    if (
      (!bankDetails.zip || bankDetails.zip.trim() === '') &&
      bankReferenceRequiredFields.includes('address') &&
      !fieldsOptional
    ) {
      errors.push('zip');
    }
    if (
      !bankDetails.authorizedFile &&
      bankReferenceRequiredFields.includes('authorizedFile') &&
      !fieldsOptional
    ) {
      errors.push('authorizedFile');
    }

    setErrorFields(errors);
    return errors.length === 0;
  };

  const handleCloseModal = () => {
    setBankDetails(EMPTY_BANK_DETAILS);
    setErrorFields([]);
    setPhoneError('');
    handleClose();
  };

  const handleAddBankDetails = () => {
    if (!validateInputs()) return;
    onSubmit(bankDetails);
    handleCloseModal();
  };

  return (
    <DillModal
      open={open}
      onClose={handleCloseModal}
      title={title ?? 'Add Bank Details'}
      maxWidth="540px"
      contentClassName="px-4 py-3 max-h-[calc(100vh-200px)] overflow-y-auto"
      footer={
        <>
          <DillButton label="Cancel" variant="secondary" onClick={handleCloseModal} />
          <DillButton
            label={submitButtonLabel ?? 'Add Bank Details'}
            variant="primary"
            onClick={handleAddBankDetails}
          />
        </>
      }>
      <div className="flex flex-col gap-4">
        <AppInputField
          id="bankName"
          label="Bank Name"
          isRequired
          placeholder="Enter Bank Name"
          errorText={errorFields.includes('bankName') ? 'Bank name is required' : ''}
          value={bankDetails.bankName}
          onTextChange={(text) => {
            handleInputChange('bankName', text);
            setErrorFields((prev) => prev.filter((field) => field !== 'bankName'));
          }}
        />
        <AppInputField
          id="accountNumber"
          label="Account Number"
          isRequired
          placeholder="Enter Account Number"
          errorText={errorFields.includes('accountNumber') ? 'Account number is required' : ''}
          value={bankDetails.accountNumber}
          onTextChange={(text) => {
            handleInputChange('accountNumber', text);
            setErrorFields((prev) => prev.filter((field) => field !== 'accountNumber'));
          }}
          dataPrivate
        />
        <AppInputField
          id="email"
          label="Contact Email"
          isRequired={!fieldsOptional}
          placeholder="Enter Email"
          errorText={
            errorFields.includes('email') && !emailRegex.test(bankDetails.email.trim())
              ? 'Email is not valid'
              : errorFields.includes('email') &&
                findBlockedTradeReferenceEmailDomain(bankDetails.email.trim(), blockedEmailDomains)
              ? blockedEmailMessage
              : errorFields.includes('email')
              ? 'Email is required'
              : ''
          }
          value={bankDetails.email}
          onTextChange={(text) => {
            handleInputChange('email', text);
            setErrorFields((prev) => prev.filter((field) => field !== 'email'));
          }}
        />
        {bankReferenceRequiredFields.includes('address') && (
          <>
            <AddressAutocompleteSelect
              isRequired={!fieldsOptional}
              label="Address"
              value={bankDetails.address}
              errorText={errorFields.includes('address') ? 'Address is required' : ''}
              onTextChange={(text) => {
                handleInputChange('address', text);
                setErrorFields((prev) => prev.filter((field) => field !== 'address'));
              }}
              onSelectSuggestion={(suggestion) => {
                const streetLine = [suggestion.streetLine, suggestion.secondary]
                  .filter(Boolean)
                  .join(' ');
                setBankDetails((prev) => ({
                  ...prev,
                  address: streetLine,
                  city: suggestion.city,
                  state: suggestion.state,
                  zip: suggestion.zipcode,
                }));
                setErrorFields((prev) =>
                  prev.filter((field) => !['address', 'city', 'state', 'zip'].includes(field))
                );
              }}
            />
            <div className="flex w-full gap-4">
              <div className="w-1/3">
                <AppInputField
                  isRequired={!fieldsOptional}
                  errorText={errorFields.includes('city') ? 'City is required' : ''}
                  id="city"
                  label="City"
                  placeholder="Enter City"
                  value={bankDetails.city}
                  onTextChange={(text) => {
                    handleInputChange('city', text);
                    setErrorFields((prev) => prev.filter((field) => field !== 'city'));
                  }}
                />
              </div>
              <div className="w-1/3">
                <StateDropDown
                  isRequired={!fieldsOptional}
                  errorText={errorFields.includes('state') ? 'State is required' : ''}
                  label="State"
                  value={bankDetails.state}
                  onTextChange={(text) => {
                    handleInputChange('state', text);
                    setErrorFields((prev) => prev.filter((field) => field !== 'state'));
                  }}
                />
              </div>
              <div className="w-1/3">
                <AppInputField
                  isRequired={!fieldsOptional}
                  errorText={errorFields.includes('zip') ? 'Zip is required' : ''}
                  id="zip"
                  label="Zip"
                  placeholder="Enter Zip"
                  value={bankDetails.zip}
                  onTextChange={(text) => {
                    handleInputChange('zip', text);
                    setErrorFields((prev) => prev.filter((field) => field !== 'zip'));
                  }}
                />
              </div>
            </div>
          </>
        )}
        {bankReferenceRequiredFields.includes('phoneNumber') && (
          <div className="flex flex-col gap-1">
            <AppInputField
              isRequired={bankReferenceRequiredFields.includes('phoneNumber') && !fieldsOptional}
              errorText={
                errorFields.includes('phoneNumber') ? phoneError || 'Phone number is required' : ''
              }
              id="phoneNumber"
              label="Phone Number"
              placeholder="XXX-XXX-XXXX"
              maxLength={12}
              value={bankDetails.phoneNumber}
              onTextChange={(text) => {
                const formatted = formatPhone(text);
                handleInputChange('phoneNumber', formatted);
                setErrorFields((prev) => prev.filter((field) => field !== 'phoneNumber'));
                setPhoneError(
                  formatted.length === 12 && !phoneRegex.test(formatted)
                    ? 'Must be in XXX-XXX-XXXX format (e.g. 555-123-4567)'
                    : ''
                );
              }}
              onBlur={() => {
                const val = bankDetails.phoneNumber.trim();
                if (val && !phoneRegex.test(val)) {
                  setErrorFields((prev) =>
                    prev.includes('phoneNumber') ? prev : [...prev, 'phoneNumber']
                  );
                  setPhoneError('Must be in XXX-XXX-XXXX format (e.g. 555-123-4567)');
                }
              }}
            />
            {!errorFields.includes('phoneNumber') && (
              <p className="text-xs text-gray-400">Format: XXX-XXX-XXXX (e.g. 555-123-4567)</p>
            )}
          </div>
        )}
        {bankReferenceRequiredFields.includes('contactPerson') && (
          <AppInputField
            isRequired={bankReferenceRequiredFields.includes('contactPerson') && !fieldsOptional}
            errorText={errorFields.includes('contactPerson') ? 'Contact person is required' : ''}
            id="contactPerson"
            label="Contact Person"
            placeholder="Enter Contact Person"
            value={bankDetails.contactPerson}
            onTextChange={(text) => {
              handleInputChange('contactPerson', text);
              setErrorFields((prev) => prev.filter((field) => field !== 'contactPerson'));
            }}
          />
        )}
        {bankReferenceRequiredFields.includes('authorizedFile') && (
          <UploadFileInputs
            isRequired={bankReferenceRequiredFields.includes('authorizedFile') && !fieldsOptional}
            errorText={errorFields.includes('authorizedFile') ? 'Authorized file is required' : ''}
            selectedFile={bankDetails.authorizedFile}
            label="Authorized File"
            onSelectFile={(data) => {
              handleInputChange('authorizedFile', data.file);
              setErrorFields((prev) => prev.filter((field) => field !== 'authorizedFile'));
            }}
          />
        )}
      </div>
    </DillModal>
  );
};

export default BankDetailsModal;
